import { useEffect, useState } from "react";
import axios from "axios";
import { Brain, Rocket, Shield, Target, Zap } from "lucide-react";
import { useEOSAuth } from "../../contexts/EOSAuthContext";
import type { Model as ModelInfo } from "../ModelInfoModal";

export type EOSModel = ModelInfo & {
  id?: string;
  _id?: string;
  is_applied?: boolean;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://engineeringofstockbackend.onrender.com";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

export function useEOSModels() {
  const { token, user } = useEOSAuth();
  const [models, setModels] = useState<EOSModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<EOSModel | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [accountBalance, setAccountBalance] = useState(0);

  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successAlertMessage, setSuccessAlertMessage] = useState("");
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedModelForInfo, setSelectedModelForInfo] = useState<EOSModel | null>(null);

  const clearError = () => {
    setError(null);
  };

  useEffect(() => {
    if (user && user.firstName === "xyz") {
      setSelectedModel(null);
      setSuccessAlertMessage(`Welcome back, ${user.firstName}!`);
      setShowSuccessAlert(true);
    }
  }, [user]);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  }, [token]);

  const initializeModels = async () => {
    try {
      const response = await api.post("/api/models/initialize");
      const responseData = response.data ?? {};

      if (responseData.success) {
        void fetchModels();
      }
    } catch {
      // noop
    }
  };

  const fetchAccountBalance = async () => {
    try {
      const response = await api.get("/api/trading/account-balance");
      const responseData = response.data ?? {};

      if (responseData.success) {
        setAccountBalance(responseData?.data?.balance ?? 0);
      }
    } catch {
      // noop
    }
  };

  const fetchModels = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/api/models");

      const modelsData = response.data?.data?.models || response.data?.models || [];
      setModels(Array.isArray(modelsData) ? modelsData : []);
    } catch (err: any) {
      setModels([]);

      let errorMessage = "Failed to fetch models. Please try again.";

      if (err.response?.status === 401) {
        errorMessage = "Authentication required. Please log in again.";
      } else if (err.response?.status === 403) {
        errorMessage = "Access denied. Please check your permissions.";
      } else if (err.response?.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchModels();
    void fetchAccountBalance();
    void initializeModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyModel = async (modelId?: string) => {
    try {
      const response = await api.post(`/api/models/apply/${modelId}`, { activate: true });
      const responseData = response.data ?? {};

      if (responseData.success) {
        void fetchModels();
        setError(null);
        const modelName = responseData?.data?.name ?? responseData?.name ?? "model";
        setSuccessMessage(`Model "${modelName}" applied successfully. Other models have been unapplied.`);
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        throw new Error(responseData.error || "Failed to apply model");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "";
      if (!errorMessage.includes("Session expired")) {
        setError("Failed to apply model. Please try again.");
      }
    }
  };

  const handleUnapplyModel = async (modelId?: string) => {
    try {
      const response = await api.post(`/api/models/unapply/${modelId}`);
      const responseData = response.data ?? {};

      if (responseData.success) {
        void fetchModels();
        setSuccessMessage(null);
      } else {
        throw new Error(responseData.error || "Failed to unapply model");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "";
      if (!errorMessage.includes("Session expired")) {
        setError("Failed to unapply model. Please try again.");
      }
    }
  };

  const handleOpenInfoModal = (model: EOSModel) => {
    setSelectedModelForInfo(model);
    setInfoModalOpen(true);
  };

  const handleCloseInfoModal = () => {
    setInfoModalOpen(false);
    setSelectedModelForInfo(null);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("en-IN").format(num);
  };

  const formatPercentage = (value: number | string) => {
    return `${parseFloat(String(value)).toFixed(1)}%`;
  };

  const formatCurrency = (amount: number) => {
    if (!amount || isNaN(amount)) {
      return "₹0";
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const hasSufficientBalance = (requiredAmount?: number) => {
    if (!requiredAmount || isNaN(requiredAmount)) {
      return false;
    }
    return accountBalance >= requiredAmount;
  };

  const getModelIcon = (modelType?: string): React.ElementType => {
    switch (modelType) {
      case "momentum":
        return Rocket;
      case "mean_reversion":
        return Target;
      case "breakout":
        return Zap;
      case "volatility":
        return Shield;
      default:
        return Brain;
    }
  };

  const getModelIconColor = (modelType?: string) => {
    switch (modelType) {
      case "momentum":
        return "text-emerald-300";
      case "mean_reversion":
        return "text-green-300";
      case "breakout":
        return "text-yellow-300";
      case "volatility":
        return "text-purple-300";
      default:
        return "text-slate-400";
    }
  };

  const getModelColor = (modelType?: string) => {
    switch (modelType) {
      case "momentum":
        return "from-emerald-500 to-emerald-600";
      case "mean_reversion":
        return "from-green-500 to-green-600";
      case "breakout":
        return "from-yellow-500 to-yellow-600";
      case "volatility":
        return "from-purple-500 to-purple-600";
      default:
        return "from-gray-500 to-gray-600";
    }
  };

  return {
    user,
    models,
    loading,
    error,
    selectedModel,
    successMessage,
    setSuccessMessage,
    accountBalance,
    showSuccessAlert,
    successAlertMessage,
    infoModalOpen,
    selectedModelForInfo,
    clearError,
    fetchModels,
    handleApplyModel,
    handleUnapplyModel,
    handleOpenInfoModal,
    handleCloseInfoModal,
    formatNumber,
    formatPercentage,
    formatCurrency,
    hasSufficientBalance,
    getModelIcon,
    getModelIconColor,
    getModelColor,
  };
}
